// UA Tracker tab (widgetboss only) — the utility allowance delivery funnel.
// One row per authority on the UA slate; Generated / Shared / Accepted /
// Paid are dropdowns saved straight to the compliance service, and each
// row's color is derived from its cells (gold = complete).
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw } from 'lucide-react';

const COMPLIANCE_API_URL =
  process.env.REACT_APP_COMPLIANCE_API ||
  'https://housing-compliance-service.onrender.com';
const COMPLIANCE_ADMIN_KEY =
  process.env.REACT_APP_COMPLIANCE_ADMIN_KEY || process.env.REACT_APP_ADMIN_API_KEY || '';

async function trackerFetch(path, options = {}) {
  const url = new URL(`${COMPLIANCE_API_URL}/api/utility-allowance${path}`, window.location.origin);
  if (COMPLIANCE_ADMIN_KEY) url.searchParams.set('key', COMPLIANCE_ADMIN_KEY);
  const res = await fetch(url.toString(), options);
  if (!res.ok) throw new Error(`Tracker API error: ${res.status}`);
  return res.json();
}

const OPTIONS = {
  generated: [
    ['yes', 'Yes'],
    ['partial', 'Partial (computed only)'],
    ['in_hand', 'No — schedule in hand'],
    ['need_schedule', 'No — need their schedule'],
  ],
  shared: [['yes', 'Yes'], ['no', 'No']],
  accepted: [['yes', 'Yes'], ['no', 'No']],
  paid: [
    ['stripe', 'Yes — Stripe'],
    ['po', 'Yes — purchase order'],
    ['demo', 'Demo (chatbot customer)'],
    ['no', 'No'],
  ],
};

const PAID_OK = ['stripe', 'po', 'demo'];

// Row color logic — mirrors the tracker doc's legend.
function rowColor(r) {
  if (r.generated === 'yes' && r.shared === 'yes' && r.accepted === 'yes' && PAID_OK.includes(r.paid)) {
    return '#ffd966'; // gold: complete
  }
  if (r.paid === 'stripe' || r.paid === 'po') return '#d9ead3'; // green: paid
  if ((r.generated === 'yes' || r.generated === 'partial') && r.shared !== 'yes') {
    return '#d9d2e9'; // purple: generated, not yet shared (the to-send pile)
  }
  if (r.generated === 'yes' || r.generated === 'partial') return '#d0e0f5'; // blue: generated + shared
  if (r.generated === 'in_hand') return '#fff2cc'; // yellow: build pending
  return '#f4cccc'; // red: waiting on their schedule
}

const LEGEND = [
  ['#ffd966', 'Gold = complete (generated + shared + accepted + paid/demo)'],
  ['#d9ead3', 'Green = paid'],
  ['#d9d2e9', 'Purple = generated, not yet shared'],
  ['#d0e0f5', 'Blue = generated + shared, not yet paid'],
  ['#fff2cc', 'Yellow = schedule in hand, build pending'],
  ['#f4cccc', 'Red = waiting on their schedule'],
];

const FIELD_LABELS = { generated: 'Generated', shared: 'Shared', accepted: 'Accepted', paid: 'Paid' };
const NO_FILTERS = { color: '', generated: '', shared: '', accepted: '', paid: '', effective: '', q: '' };

// Display order: like statuses together (gold, green, blue, yellow, red) —
// within a group the API's date order carries through unchanged.
const COLOR_ORDER = ['#ffd966', '#d9ead3', '#d9d2e9', '#d0e0f5', '#fff2cc', '#f4cccc'];
function groupByStatus(rs) {
  // Primary: color group. Secondary: unshared before shared, so a paid (green)
  // row still owed a send-out surfaces at the top of its block.
  return [...rs].sort((a, b) =>
    (COLOR_ORDER.indexOf(rowColor(a)) - COLOR_ORDER.indexOf(rowColor(b)))
    || ((a.shared === 'yes' ? 1 : 0) - (b.shared === 'yes' ? 1 : 0))
  );
}

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T12:00:00');
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

const UATrackerTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [savingSlug, setSavingSlug] = useState(null);
  const [filters, setFilters] = useState(NO_FILTERS);
  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await trackerFetch('/admin/tracker');
      setRows(data.rows || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const update = async (slug, field, value) => {
    const prev = rows;
    setRows((rs) => rs.map((r) => (r.slug === slug ? { ...r, [field]: value } : r)));
    setSavingSlug(slug);
    try {
      await trackerFetch(`/admin/tracker/${encodeURIComponent(slug)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
    } catch (e) {
      setRows(prev); // roll back on failure
      setError(`Save failed for ${slug}: ${e.message}`);
    } finally {
      setSavingSlug(null);
    }
  };

  const cellSelect = (r, field) => (
    <select
      value={r[field]}
      onChange={(e) => update(r.slug, field, e.target.value)}
      aria-label={`${FIELD_LABELS[field]} for ${r.name}`}
      className="w-full bg-transparent text-xs border border-black/10 rounded px-1 py-0.5 cursor-pointer"
    >
      {OPTIONS[field].map(([v, label]) => (
        <option key={v} value={v}>{label}</option>
      ))}
    </select>
  );

  const goldCount = rows.filter((r) => rowColor(r) === '#ffd966').length;
  const paidCount = rows.filter((r) => r.paid === 'stripe' || r.paid === 'po').length;

  // Filters: every set filter must match. `color` is the row's status group
  // (legend chip); the four dropdowns match the row's cell values exactly.
  const q = filters.q.trim().toLowerCase();
  const visible = rows.filter((r) =>
    (!filters.color || rowColor(r) === filters.color)
    && Object.keys(FIELD_LABELS).every((f) => !filters[f] || r[f] === filters[f])
    && (!filters.effective || r.effective_date === filters.effective)
    && (!q || `${r.name} ${r.slug}`.toLowerCase().includes(q))
  );
  const anyFilter = Object.values(filters).some(Boolean);
  const effectiveDates = [...new Set(rows.map((r) => r.effective_date).filter(Boolean))].sort();
  const countWhere = (test) => rows.filter(test).length;

  const filterSelect = (field) => (
    <label key={field} className="flex items-center gap-1.5 text-xs text-gray-600">
      {FIELD_LABELS[field]}
      <select
        value={filters[field]}
        onChange={(e) => setFilter(field, e.target.value)}
        aria-label={`Filter by ${FIELD_LABELS[field].toLowerCase()}`}
        className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white cursor-pointer"
      >
        <option value="">All ({rows.length})</option>
        {OPTIONS[field].map(([v, label]) => (
          <option key={v} value={v}>{label} ({countWhere((r) => r[field] === v)})</option>
        ))}
      </select>
    </label>
  );

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">UA Tracker</h2>
          <p className="text-sm text-gray-500">
            {rows.length} authorities · {paidCount} paid · {goldCount} complete
          </p>
        </div>
        <button
          onClick={load}
          className="flex items-center gap-1.5 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} /> Refresh
        </button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3 text-xs text-gray-700">
        {LEGEND.map(([color, label]) => {
          const on = filters.color === color;
          return (
            <button
              key={color}
              type="button"
              onClick={() => setFilter('color', on ? '' : color)}
              aria-pressed={on}
              className={`px-2 py-0.5 rounded border cursor-pointer ${on ? 'border-gray-900 ring-1 ring-gray-900' : 'border-transparent hover:border-gray-400'}`}
              style={{ background: color }}
            >
              {label} ({countWhere((r) => rowColor(r) === color)})
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        {Object.keys(FIELD_LABELS).map(filterSelect)}
        <label className="flex items-center gap-1.5 text-xs text-gray-600">
          Effective
          <select
            value={filters.effective}
            onChange={(e) => setFilter('effective', e.target.value)}
            aria-label="Filter by effective date"
            className="text-xs border border-gray-300 rounded px-1.5 py-1 bg-white cursor-pointer"
          >
            <option value="">All dates</option>
            {effectiveDates.map((d) => (
              <option key={d} value={d}>{fmtDate(d)} ({countWhere((r) => r.effective_date === d)})</option>
            ))}
          </select>
        </label>
        <input
          type="search"
          value={filters.q}
          onChange={(e) => setFilter('q', e.target.value)}
          placeholder="Search authority"
          aria-label="Search authorities"
          className="text-xs border border-gray-300 rounded px-2 py-1 w-44"
        />
        {anyFilter && (
          <button
            type="button"
            onClick={() => setFilters(NO_FILTERS)}
            className="text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50"
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-gray-500 ml-auto">Showing {visible.length} of {rows.length}</span>
      </div>

      {error && <div className="mb-3 text-sm text-red-600">{error}</div>}

      <div className="overflow-x-auto">
        <table className="w-full text-sm border border-gray-200">
          <thead>
            <tr className="bg-gray-900 text-white text-left">
              <th className="px-3 py-2 font-medium">Housing Authority</th>
              <th className="px-3 py-2 font-medium whitespace-nowrap">Effective Date</th>
              <th className="px-3 py-2 font-medium">Generated</th>
              <th className="px-3 py-2 font-medium">Shared</th>
              <th className="px-3 py-2 font-medium">Accepted</th>
              <th className="px-3 py-2 font-medium">Paid</th>
            </tr>
          </thead>
          <tbody>
            {groupByStatus(visible).map((r) => (
              <tr key={r.slug} style={{ background: rowColor(r) }} className="border-t border-black/10">
                <td className="px-3 py-1.5 font-medium text-gray-900 whitespace-nowrap">
                  {r.name}
                  {savingSlug === r.slug && <span className="ml-1.5 text-[10px] text-gray-500">saving…</span>}
                </td>
                <td className="px-3 py-1.5 whitespace-nowrap text-gray-700">{fmtDate(r.effective_date)}</td>
                <td className="px-2 py-1.5">{cellSelect(r, 'generated')}</td>
                <td className="px-2 py-1.5">{cellSelect(r, 'shared')}</td>
                <td className="px-2 py-1.5">{cellSelect(r, 'accepted')}</td>
                <td className="px-2 py-1.5">{cellSelect(r, 'paid')}</td>
              </tr>
            ))}
            {!rows.length && !loading && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-400">No rows.</td></tr>
            )}
            {rows.length > 0 && !visible.length && (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-gray-500">No authorities match these filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default UATrackerTab;
